import { useEffect, useState } from 'react'

/**
 * Le chronomètre d'une question — RG-2.28, 06 §6.3.
 *
 * **Par question, jamais global.** C'est le rythme qu'on entraîne : au
 * concours, celui qui s'enlise sur la question 3 perd les questions 18 à 25
 * sans jamais les avoir lues. Un chrono global permettrait précisément cet
 * enlisement.
 *
 * **Il se REMONTE à chaque question, il ne se réinitialise pas.** Le parent lui
 * passe une `key` : React le détruit et le recrée, l'état repart de zéro tout
 * seul. Une première version remettait le compteur dans un effet — ce qui
 * marchait, mais faisait un rendu de trop à chaque question et laissait, entre
 * les deux, un compteur affichant la valeur de la question précédente.
 *
 * **Jamais de clignotement** (06 §6.3). La couleur change — neutre, puis
 * `attente` sous 20 %, puis `faux` sous 10 % — et c'est tout. Un compteur qui
 * clignote sous pression gêne tout le monde et met en danger les personnes
 * photosensibles ; il n'apporte rien qu'une couleur n'apporte déjà.
 */
export function Chronometre({
  secondes,
  onExpiration,
}: {
  secondes: number
  onExpiration: () => void
}) {
  const [restant, setRestant] = useState(secondes)

  useEffect(() => {
    const debut = Date.now()

    const battement = setInterval(() => {
      // On recalcule depuis l'horloge plutôt que de décrémenter : un onglet
      // mis en arrière-plan gèle les intervalles, et un compteur décrémenté
      // offrirait alors du temps supplémentaire à qui change d'application.
      const reste = Math.max(0, secondes - Math.floor((Date.now() - debut) / 1000))

      setRestant(reste)

      if (reste === 0) {
        clearInterval(battement)
        onExpiration()
      }
    }, 250)

    return () => {
      clearInterval(battement)
    }
    // `onExpiration` change à chaque rendu du parent ; le mettre en dépendance
    // relancerait l'intervalle — et donc rallongerait le temps — à chaque fois
    // que le parent se redessine. Le composant vit le temps d'une question.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondes])

  const part = restant / secondes

  const couleur =
    part <= 0.1 ? 'var(--faux-fort)' : part <= 0.2 ? 'var(--attente-fort)' : 'var(--texte-2)'

  return (
    <span
      className="text-[15px] font-bold tabular-nums"
      style={{ color: couleur }}
      role="timer"
      aria-live="off"
      aria-label={`${String(restant)} secondes restantes`}
    >
      {restant} s
    </span>
  )
}
